import React, { useState, useEffect, useContext, useMemo } from "react";
import Preloader from "../src/pages/Pre";
import Navbar from "./pages/Navbar";
import Home from "./pages/Home/Home";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import ScrollToTop from "./pages/ScrollToTop";
import "bootstrap/dist/css/bootstrap.min.css";
import "./style.css";
import "./App.css";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Toaster } from "react-hot-toast";
import { Helmet } from "react-helmet";
import { MyContext } from "./Context/MyContext";

function App() {
  const [load, updateLoad] = useState(true);
  const [hashValue, setHashValue] = useState("");
  const { theme } = useContext(MyContext);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: theme,
          primary: {
            main: theme === "dark" ? "#3aa0ff" : "#148af8",
          },
          background: {
            default: theme === "dark" ? "#070b14" : "#F7F9FC",
            paper: theme === "dark" ? "#121a28" : "#FFFFFF",
          },
          text: {
            primary: theme === "dark" ? "#F4F7FB" : "#0B1220",
            secondary: theme === "dark" ? "#9AA8BC" : "#3D4A5C",
          },
        },
        typography: {
          fontFamily: "Manrope, sans-serif",
        },
        shape: {
          borderRadius: 4,
        },
      }),
    [theme]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      updateLoad(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const checkHashValue = () => {
      const hash = window.location.hash;
      if (hash) {
        setHashValue(hash.substring(1));
      } else {
        setHashValue("");
      }
    };

    checkHashValue();

    window.addEventListener("hashchange", checkHashValue);
    return () => {
      window.removeEventListener("hashchange", checkHashValue);
    };
  }, []);

  const renderHelmet = () => {
    let title =
      "DigitaliNation | Software, Marketing, AI Automations & GoHighLevel";
    let description =
      "DigitaliNation is a premium software and growth studio. We build websites, apps, marketing, AI automations, and GoHighLevel systems that help businesses win customers.";
    let keywords =
      "software development, marketing, graphic design, AI automation, GoHighLevel, DigitaliNation, web development, virtual assistant";

    switch (hashValue) {
      case "services":
        title = "Services | DigitaliNation";
        description =
          "Web solutions, mobile apps, social, email, graphic design, AI automations, GoHighLevel, and virtual assistance from DigitaliNation.";
        keywords =
          "services, software development, marketing, graphic design, DigitaliNation";
        break;
      case "process":
        title = "How We Work | DigitaliNation";
        description =
          "Discover, design, build, and grow. A clear process for shipping digital work that converts.";
        keywords = "process, how we work, DigitaliNation";
        break;
      case "about":
        title = "About | DigitaliNation";
        description =
          "DigitaliNation is a software and marketing studio founded by Haroon Absar, helping businesses grow in the digital landscape.";
        keywords = "about, team, DigitaliNation";
        break;
      case "book-appointment":
        title = "Book a Call | DigitaliNation";
        description =
          "Schedule a consultation with DigitaliNation to discuss your project and the services that fit.";
        keywords = "book appointment, consultation, DigitaliNation";
        break;
      default:
        break;
    }

    return (
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content="https://www.digitalination.com/" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Helmet>
    );
  };

  return (
    <Router>
      {renderHelmet()}
      <Preloader load={load} />
      <div className="App" id={load ? "no-scroll" : "scroll"}>
        <ThemeProvider theme={muiTheme}>
          <CssBaseline />
          <Navbar />
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </ThemeProvider>
      </div>
      <Toaster position="top-center" reverseOrder={false} />
    </Router>
  );
}

export default App;
